<script>
    import { toast } from "@zerodevx/svelte-toast";
    import { getAccessToken } from "../auth";
    import Modal from "../components/Modal.svelte";
    import { environment, isAuthenticated } from "../stores";
    import {
        toastThemeError,
        toastThemeErrorDismissable,
        toastThemeSuccess,
        toastThemeSuccessDismissable,
    } from "../toasts";
    import SearchMultiSelect from "./SearchMultiSelect.svelte";
    import  ChevronDown from "svelte-material-icons/ChevronDown.svelte";


    let userServiceURL = "https://dev-user-service.livelyforest-c696615c.westeurope.azurecontainerapps.io";
    if ($environment === "prod") {
        userServiceURL = "https://ext-prod-user-service.livelyforest-c696615c.westeurope.azurecontainerapps.io";
    }

    export let organization;

    let users = [];
    let teams = [];
    let addTeams = [];
    let selectedTeams = {};
    let refreshMultiSelect = false;
    let editTeams = [];
    let selectedEditTeams = {};
    let inputError = {
        passwordError: false,
        nameError: false,
        emailError: false,
        teams: false,
    };
    let selectedUsers = new Set();
    let userForEdit = null;
    let userForDeletion = null;
    let editName = "";

    $: {
        if (organization && $isAuthenticated) {
            refreshData(organization._id);
        }
    }

    async function clearData() {
        selectedTeams = {};
        addTeams = [];
    }

    async function fetchOrganizationsUsers(orgId) {
        const authToken = await getAccessToken();
        const response = await fetch(`${userServiceURL}/organization/${orgId}/users`, {
            method: "GET",
            headers: {
                Authorization: "Bearer " + authToken,
                Accept: "*/*",
            },
        });
        return response;
    }

    async function fetchOrganizationsTeams(orgId) {
        const authToken = await getAccessToken();
        const response = await fetch(`${userServiceURL}/organization/${orgId}/teams`, {
            method: "GET",
            headers: {
                Authorization: "Bearer " + authToken,
                Accept: "*/*",
            },
        });
        return response;
    }

    async function refreshData(organizationId) {
        clearData()
        const usersResponse = await fetchOrganizationsUsers(organizationId);
        const userData = await usersResponse.json();
        if (usersResponse.status === 200) {
            users = userData;
        }
        else {
            toast.push(userData.message, toastThemeError);
        }

        refreshMultiSelect = true;
        const teamsResponse = await fetchOrganizationsTeams(organizationId);
        const teamsData = await teamsResponse.json()
        if (teamsResponse.status === 200) {
            teams = teamsData;
        }
        else {
            toast.push(teamsData.message, toastThemeError);
        }
        refreshMultiSelect = false;
    }


    async function createUser(body) {
        const authToken = await getAccessToken();
        const userBody = JSON.stringify(body);
        const response = await fetch(`${userServiceURL}/user`, {
            method: "POST",
            headers: {
                Authorization: "Bearer " + authToken,
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            body: userBody,
        });
        return response;
    }

    async function deleteUser(userId) {
        const authToken = await getAccessToken();
        const response = await fetch(`${userServiceURL}/organization/${organization._id}/user/${userId}`, {
            method: "DELETE",
            headers: {
                Authorization: "Bearer " + authToken,
                Accept: "application/json",
                "Content-Type": "application/json",
            },
        });
        return response;
    }

    async function updateUser(user) {
        const authToken = await getAccessToken();
        let newName = user.name;
        if (editName.length !== 0){
            newName = editName;
        }
        const body = await JSON.stringify({ name: newName, teamIds: editTeams, organizationId: organization._id });
        const response = await fetch(`${userServiceURL}/user/${user._id}`, {
            method: "put",
            headers: {
                Authorization: "Bearer " + authToken,
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            body,
        });
        return response;
    }

    async function sendEditUser(user) {
        const updateResponse = await updateUser(user);
        const updateData = await updateResponse.json();
        if (updateResponse.status === 200) {
            toast.push(updateData.message, toastThemeSuccess);
            editName = "";
            userForEdit = null;
            await refreshData(organization._id);
        }
        else {
            toast.push("Failed to update", toastThemeError);
        }
    }

    async function handleDelete(user) {
            const response = await deleteUser(user._id);
            if(response.status === 200) {
                toast.push(`Removed user ${user.name} from organization`, toastThemeSuccess);
                userForDeletion = null;
                await refreshData(organization._id);
            }
            else {
                toast.push("Failed to remove", toastThemeError);
            }
    }

    async function handleForm(event) {
        const formData = new FormData(event.target);
        let body = {};
        for (let field of formData) {
            const [key, value] = field;
            const errorKey = key + "Error";
            switch (key) {
                case "password":
                    if (value.length < 8) {
                        inputError[errorKey] = "Must at least have 8 characters";
                        continue;
                    }
                    if (
                        !/^(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z])([a-zA-Z0-9!@#$€%^&*()_+\-=[\]{};':"\\|,.<>/?]+)$/.test(
                            value
                        )
                    ) {
                        inputError[errorKey] = "Must include lower case (a-z), upper case (A-Z) and numbers (0-9)";
                        continue;
                    }
                    break;
                case "name":
                    value.trim();
                    if (!/^(?=.*[\p{L}\p{N}])[\p{L}\p{M}'\s_\-:\d()/]{1,50}$/u.test(value)) {
                        inputError[errorKey] = "Invalid character for name";
                        continue;
                    }
                    break;
                default:
                    value.trim();
                    if (value.length < 1) {
                        inputError[errorKey] = "Must be at least 1 characters";
                        continue;
                    }
                    if (value.length > 50) {
                        inputError[errorKey] = "Can not be longer than 50 characters";
                        continue;
                    }
                    break;
            }
            inputError[errorKey] = false;
            body[key] = value;
        }
        body.organizationIds = [organization._id];
        if (addTeams.length <= 0) {
            inputError['teams'] = "Must select at least one team for user";
            return;
        }
        else {
            inputError['teams'] = false;
        }
        
        if (Object.keys(inputError).some((error) => inputError[error] !== false)) {
            return;
        }
        body.teamIds = addTeams;
        const response = await createUser(body);
        const data = await response.json();
        if(response.status === 200){
            event.target.reset();
            toast.push(data.message, toastThemeSuccessDismissable);
            await refreshData(organization._id);
        }
        else{
            if (data.length > 0) {
                data.forEach((mes) => toast.push(mes.data, toastThemeErrorDismissable));
            }
            else {
                toast.push(data.message, toastThemeErrorDismissable);
            }
        }
    }

    function handleUser(user) {
        if (selectedUsers.has(user._id)) {
            selectedUsers.delete(user._id);
            selectedUsers = selectedUsers;
        } else {
            selectedUsers = selectedUsers.add(user._id);
        }
    }

    function handleEdit(user) {
        userForEdit = user;
        editTeams = user.teams.filter(team => team.organizationId == organization._id).map(team => team._id);
        editTeams.forEach(team => {
            selectedEditTeams[team] = {value: team, name: ""};
        });
    }

    function clearEdit() {
        editTeams = [];
        selectedEditTeams = {};
        userForEdit = null;
        editName = "";
    }

    function remove(value) {
        const { [value]: val, ...rest } = selectedTeams;
        selectedTeams = rest;
    }

    function removeEditTeam(value) {
        const { [value]: val, ...rest } = selectedEditTeams;
        selectedEditTeams = rest;
    }

</script>

<main-window>
    <admin-body>
        <collapse>
            <add-user-card>
                <form id="createUser" autocomplete="off" on:submit|preventDefault={(e) => handleForm(e)}>
                    <label for="role">Role</label>
                    <select name="role" form="createUser">
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                    </select>
                    <label for="name">Name</label>
                    <input autocomplete="off" name="name" type="text" />
                    {#if inputError.nameError !== false}
                        <span>{inputError.nameError}</span>
                    {/if}
                    <label for="email">Email</label>
                    <input autocomplete="new-password" name="email" type="email" />
                    {#if inputError.emailError !== false}
                        <span>{inputError.emailError}</span>
                    {/if}
                    <label for="password">Password</label>
                    <input autocomplete="new-password" name="password" type="password" />
                    {#if inputError.passwordError !== false}
                        <span>{inputError.passwordError}</span>
                    {/if}
                    <label for="teams">Teams</label>
                    <select-container>
                        {#if teams.length > 0 && !refreshMultiSelect}
                        <SearchMultiSelect id={organization._id} bind:selected={selectedTeams} bind:value={addTeams}>
                            {#each teams as team}
                            <option value={team._id}>{team.name}</option>
                            {/each}
                        </SearchMultiSelect>
                        {/if}
                        <div class="token-container">
                            {#if addTeams && addTeams.length > 0}
                            {#each addTeams as s}
                                <div class="token" data-id={s}>
                                    <span>{teams.find((team) => team._id === s).name}</span>
                                    <div class="token-remove" title="Remove {s}" on:click={() => remove(s)} on:keypress={()=> remove(s)}>
                                        <svg
                                        class="icon-clear"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24">
                                        <path
                                        d={"M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"} />
                                        </svg>
                                    </div>
                                </div>
                            {/each}
                            {/if}
                        </div>
                    </select-container>
                    {#if inputError.teams !== false}
                        <span>{inputError.teams}</span>
                    {/if}
                    <submit-form>
                        <span>
                        </span>
                        <button style="margin-top: 16px;" type="submit">Add user</button>
                    </submit-form>
                </form>
            </add-user-card>
        </collapse>             
        {#each users as user}
            <user-card>
                <user-header on:click={() => handleUser(user)} on:keypress={() => handleUser(user)}>
                    <flex>
                        <span
                            style={selectedUsers.has(user._id) ? "transform: rotateX(180deg)" : "" }
                            on:click|stopPropagation={() => handleUser(user)} on:keypress|stopPropagation={() => handleUser(user)}
                        >
                            <ChevronDown size={25} />
                        </span>
                        <p>{user.name}</p>
                        <role-text>{`(${user.role})`}</role-text>
                    </flex>
                    <span>
                        <button on:click|stopPropagation={() => handleEdit(user)}>Edit</button>
                        <button on:click|stopPropagation={() => (userForDeletion = user)}>Remove</button>
                    </span>
                </user-header>
                <user-content class={selectedUsers.has(user._id) ? "active" : ""}>
                    <teams-container>
                        <b>Teams:</b>
                        {#each user.teams as team}
                            {#if team.organizationId == organization._id}
                                <team-tag>{team.name}</team-tag>
                            {/if}
                        {/each}
                    </teams-container>
                    <flex>
                        <b>Email:</b>
                        <p>{user.email}</p>
                    </flex>
                </user-content>
            </user-card>
        {/each}
    </admin-body>
    {#if userForDeletion != null}
        <Modal on:close={() => (userForDeletion = null)} on:ok={() => handleDelete(userForDeletion)}>
            <h1>Are you sure you want to remove user from organization?</h1>
            <h2>{userForDeletion.name}</h2>
        </Modal>
    {/if}
    {#if userForEdit != null}
        <Modal on:close={() => clearEdit()} on:ok={() => sendEditUser(userForEdit)}>
            <h2>Edit user</h2>
            <label for="newName">New name</label>
            <input name="newName" type="text" bind:value={editName} />
            <label for="teams">Teams</label>
                {#if !refreshMultiSelect}
                <SearchMultiSelect id={"teamSelect"} bind:selected={selectedEditTeams} bind:value={editTeams}>
                    {#each teams as team}
                        <option value={team._id}>{team.name}</option>
                    {/each}
                </SearchMultiSelect>
                {/if}
                <div class="token-container">
                    {#if editTeams && editTeams.length > 0}
                        {#each editTeams as s}
                            <div class="token" data-id={s}>
                                <span>{teams.find((team) => team._id === s)?.name}</span>
                                <div class="token-remove" title="Remove {s}" on:click={() => removeEditTeam(s)} on:keypress={() => removeEditTeam(s)}>
                                    <svg
                                        class="icon-clear"
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24">
                                        <path
                                            d={"M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"} />
                                    </svg>
                                </div>
                            </div>
                        {/each}
                    {/if}
                </div>
        </Modal>
    {/if}
</main-window>

<style>
    h2 {
        text-transform: capitalize;
    }

    select {
        background-color: white;
        border: none;
    }

    admin-body {
        gap: 0.5rem;
        display: flex;
        flex-direction: column;
    }

    user-card {
        display: flex;
        flex-direction: column;
        border: solid 2px black;
        padding: 1.25rem 1rem;
        overflow: hidden;
    }
    user-card span {
        margin: 0;
        margin-left: auto;
    }

    user-header {
        cursor: pointer;
        display: flex;
        flex: 1;
        cursor: pointer;
    }

    role-text {
        color: gray;
        font-size: 1rem;
        opacity: 50%;
    }

    user-content {
        overflow: hidden;
        max-height: 0px;
        transition: max-height 200ms ease-out;
        display: flex;
        flex-direction: column;
        align-items: start;
        gap: 0.25rem;
    }

    flex {
        display: flex;
        gap: 0.25rem;
        align-items: center;
    }

    flex > span {
        transition: ease-in 250ms transform;
        cursor: pointer;
    }

    teams-container {
        display: flex;
        align-items: baseline;
        gap: 0.25rem;
    }
    p {
        margin: 0;
        margin-right: 6px;
    }
    .active {
        max-height: 90px;
    }

    team-tag {
        border: 2px solid black;
        border-radius: 6px;
        display: flex;
        align-items: center;
        padding: 6px 4px;
        font-weight: bold;
    }

    add-user-card {
        display: flex;
        flex-direction: column;
        padding: 1.25rem 1rem 10px 1rem;
        transition: max-height 0.2s ease-out;
    }

    label {
        margin: 0.25rem;
        text-align: left;
    }

    form > span {
        color: red;
        margin-top: -0.25rem;
        margin-left: 0.25rem;
        font-size: 0.85rem;
    }

    input {
        width: 100%;
    }

    collapse {
        display: flex;
        flex-direction: column;
        border: solid 2px black;
    }

    form {
        display: flex;
        flex-direction: column;
        align-items: start;
    }

    button {
        margin: 0px;
    }

    submit-form {
        display: flex;
        justify-content: space-between;
        width: 100%;
        align-items: end;
    }

    select-container {
        display: flex;
        justify-content: space-between;
        width: 100%;
        gap:20%;
        align-items: flex-start;
    }

    .token-container {
        display: flex;
        align-items: flex-start;
        padding: 0 .5rem;
        flex-wrap: wrap;
        background-color: #f9f7f5;
        border: solid 1px #e7e4e2;
        flex: 1;
        min-height: 10rem;
        align-content: flex-start;
    }
    .token {
        align-items: center;
        background-color: hsl(214, 17%, 92%);
        border-radius: 0.5rem;
        display: flex;
        margin: 0.25rem 0.5rem 0.25rem 0;
        max-height: 1.3rem;
        padding: 0.25rem 0.5rem 0.25rem 0.5rem;
        transition: background-color 0.3s;
        white-space: nowrap;
    }
    .token:hover {
        background-color: hsl(214, 15%, 88%);
    }
    .token-remove {
        align-items: center;
        background-color: hsl(214, 15%, 55%);
        border-radius: 50%;
        color: hsl(214, 17%, 92%);
        display: flex;
        justify-content: center;
        height: 1.25rem;
        margin-left: 0.25rem;
        min-width: 1.25rem;
    }
    .token-remove:hover {
        background-color: hsl(215, 21%, 43%);
        cursor: pointer;
    }
</style>
